import * as R from 'ramda';
import React, {useState} from 'react';
import {flatMap} from 'rxjs/operators';

import {translate} from 'iss/strings';
import {addActivationParty, removeActivationParty} from 'iss/service/EvaluationActivationService';
import {runBatchOps} from 'iss/utils/batchOps';
import {ActivationList} from 'iss/widgets/ActivationList';
import {FlexItem, FlexRow} from 'iss/widgets/Flex';
import {RadioGroup, ValidationErrorBox} from 'iss/widgets/Forms';
import {ActivationPartySelector, PartyActivationTypeSelector, PartySelector} from 'iss/widgets/Selectors';
import {ModalForm} from 'iss/widgets/batchOps/ModalForm';
import {ConditionalFormSection} from 'iss/widgets/form/ConditionalFormSection';
import {formatActivationParty} from 'iss/widgets/format/Party';
import {listFailuresWithMessage} from 'iss/widgets/batchOps/activation/BatchOpFailure';
import validators, {validatorFrom} from 'iss/widgets/form/validation/validators';
import {partyActivationTypes} from 'iss/service/ActivationDataService';

function PartyModificationSettings({operationTargets, onCancel, startOperation}) {
  const existingParties = getActivationParties(operationTargets);

  return <ModalForm proceedText={translate('activationOperations.modifyParties.submitText')}
    onSubmit={(formData) => startOperation(getOperation(operationTargets, formData))}
    onCancel={onCancel}>
    <RadioGroup inline id="action" label="activationOperations.modifyParties.chooseAction"
      validate={validators.chooseOne}
      valuePath="action" options={[
      {value: "add", label: translate('activationOperations.modifyParties.addParty')},
      {value: "remove", label: translate('activationOperations.modifyParties.removeParty')},
      {value: "replace", label: translate('activationOperations.modifyParties.replaceParty')}
      ]}
    />
    <AddPartiesSection showWhen={R.propEq('add', 'action')} operationTargets={operationTargets} />
    <RemovePartiesSection showWhen={R.propEq('remove', 'action')} operationTargets={operationTargets} existingParties={existingParties} />
    <ReplacePartiesSection showWhen={R.propEq('replace', 'action')} operationTargets={operationTargets} existingParties={existingParties} />
    <ActivationList activations={operationTargets} compact extraColumns={[{
      header: translate('activationOperations.modifyParties.parties'),
      accessor: ({evaluationParties}) => evaluationParties.map(party => <div key={party.id}>{formatActivationParty(party)}</div>)
    }]}/>
  </ModalForm>;
}

const getActivationParties = R.pipe(
  R.map(R.prop('evaluationParties')),
  R.unnest,
  R.uniqBy(R.prop('id'))
);


function AddPartiesSection({showWhen}) {
  const [selectedTypeIsInformant, setSelectedPartyActivationType] = useState(false);

  return <ConditionalFormSection valuePath="addParty" showWhen={showWhen}>
    <FlexRow flexAllChildren>
      <PartyActivationTypeSelector id="activationType"
        valuePath="activationType"
        label="activationOperations.modifyParties.partyRole"
        validate={validators.required}
        onChange={(value) => setSelectedPartyActivationType(value === partyActivationTypes.informant)}/>
      <ActivationPartySelector id="party"
        valuePath="party" partyTypeValuePath="activationType"
        label="activationOperations.modifyParties.partyToAdd"
        validate={validators.required} clearable={false}
        includeEmailOnlyUsers={selectedTypeIsInformant}
        />
    </FlexRow>
  </ConditionalFormSection>;
}

function RemovePartiesSection({showWhen, existingParties}) {
  return <ConditionalFormSection valuePath="removeParty" showWhen={showWhen}>
    <PartySelector id="party" parties={existingParties} valuePath="party"  label="activationOperations.modifyParties.partyToRemove"
      validate={validators.required} clearable={false} />
  </ConditionalFormSection>;
}

function replacementIsNotReplacedParty(data) {
  if (!data) return false;
  const {partyToReplace, partyReplacement} = data;
  return partyToReplace && partyReplacement && partyToReplace.id != partyReplacement.id;
}

function ReplacePartiesSection({showWhen, existingParties}) {
  return <ConditionalFormSection valuePath="replaceParty" showWhen={showWhen}>
    <FlexRow flexAllChildren>
      <PartySelector id="partyToReplace" parties={existingParties} valuePath="partyToReplace"
        label="activationOperations.modifyParties.partyToReplace"
        validate={validators.required} clearable={false} />
      <FlexItem>
        <PartySelector id="partyReplacement" valuePath="partyReplacement"
          label="activationOperations.modifyParties.partyReplacement"
          validate={validators.required} clearable={false} />
        <ValidationErrorBox validate={
          validatorFrom(replacementIsNotReplacedParty,'activationOperations.modifyParties.partyShouldNotReplaceItself')
        } />
      </FlexItem>
    </FlexRow>
  </ConditionalFormSection>;
}

function getOperation(targets, formData) {
  switch(formData.action) {
    case 'add': return addParty(targets, formData.addParty);
    case 'remove': return removeParty(targets, formData.removeParty);
    case 'replace': return replaceParty(targets, formData.replaceParty);
  }
}

function addParty(targets, {party, activationType}) {
  const ops = targets.map((target) => ({
    operation: addActivationParty(target.id, {id: party.id, activationType}),
    activation: target
  }));
  return runBatchOps(ops);
}
function removeParty(targets, {party}) {
  const ops = targets.filter(hasParty(party))
  .map((target) => ({
    operation: removeActivationParty(target.id, party.id),
    activation: target
  }));
  return runBatchOps(ops);
}
function replaceParty(targets, {partyReplacement, partyToReplace}) {
  const ops = targets.filter(hasParty(partyToReplace))
    .map(target => {
      const {activationType} = R.find(R.propEq(partyToReplace.id, 'id'), target.evaluationParties);
      const operation = addActivationParty(target.id, {id: partyReplacement.id, activationType})
        .pipe(flatMap(()=> removeActivationParty(target.id, partyToReplace.id)));

      return { operation, activation: target };
    });
  return runBatchOps(ops);
}

function hasParty(party) {
  return R.pipe(
    R.prop('evaluationParties'),
    R.any(R.propEq(party.id, 'id'))
  );
}

const modifyParties = {
  id: "modifyParties",
  title: "activationOperations.modifyParties.title",
  progressMessage: "activationOperations.modifyParties.progressMessage",
  components: {
    settings: PartyModificationSettings,
    success: <p>{translate("activationOperations.modifyParties.success")}</p>,
    failure: listFailuresWithMessage("activationOperations.modifyParties.failure")
  },
};

export default modifyParties;



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/batchOps/activation/modifyParties.jsx